import {
  Box,
  Chip,
  IconButton, Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ILegalRequirement } from '../../../../Interfaces/ILegalRequirement';
import { IConfig } from '../../../../Interfaces/IConfig';
import { ILegalTransactionFullObject } from '../../../../Interfaces/ILegalTransaction';
import { ILegalPhaseFullObject } from '../../../../Interfaces/ILegalPhase';
import { Download, UploadFile } from '@mui/icons-material';
import { IDocument } from '../../../../Interfaces/IDocument';
import Typography from '@mui/material/Typography';
import { LegalRequirementDocumentDialogs } from './LegalRequirementDocumentDialogs';

interface IProps {
  configObject: IConfig;
  legalTransaction: ILegalTransactionFullObject;
  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
}

export const LegalPhaseDocumentView: React.FC<IProps> = ({
  configObject,
  legalTransaction,
  legalPhaseFullObjectArray,
}) => {
  const [currentRequirements, setCurrentRequirements] = useState<
    ILegalRequirement[]
  >([]);
  const [currentReq, setCurrentReq] = useState<ILegalRequirement | null>(
    null
  );
  const [documentArrayClient, setDocumentArrayClient] = useState<IDocument[]>(
    []
  );
  const [documentArrayEmployees, setDocumentArrayEmployees] = useState<IDocument[]>(
    []
  );

  const [isOpenClient, setIsOpenClient] = useState(false);
  const [isOpenEmployees, setIsOpenEmployees] = useState(false);
  const [isOpenUpload, setIsOpenUpload] = useState(false);

  const handleOpenEmployees = (localReq: ILegalRequirement) => {
    setCurrentReq(localReq);
    setDocumentArrayEmployees(
      localReq.DocumentArrayEmployees === undefined
        ? []
        : localReq.DocumentArrayEmployees
    );
    setIsOpenEmployees(true);
  };

  const handleOpenClient = (localReq: ILegalRequirement) => {
    setCurrentReq(localReq);
    setDocumentArrayClient(
      localReq.DocumentArrayClient === undefined
        ? []
        : localReq.DocumentArrayClient
    );
    setIsOpenClient(true)
  };

  const handleCloseClient = () => {
    const foundItem = documentArrayClient.find(x => x.idDocument < 0);

    if (!foundItem) {
      setIsOpenClient(false);
    } else {
      setIsOpenClient(false);
      setIsOpenUpload(true);
    }
  };

  const renderDocumentList = (
    documents: IDocument[] | undefined,
    canUpload: boolean
  ) => {
    if (!documents || documents.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" component="span">
          Keine Dateien vorhanden
        </Typography>
      );
    }


    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={0.5}
      >
        {documents.map((document, index) => (
          <Chip
            key={`${document.FileName}-${index}`}
            label={document.FileName}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  useEffect(() => {
    const tmpArray: ILegalRequirement[] = [];

    legalPhaseFullObjectArray.map(currentPhase =>
      currentPhase.LegalRequirementArray.map(currentReq => {
        if (
          Boolean(currentReq.canUploadFilesClient) ||
          Boolean(currentReq.canUploadFilesEmployees)
        ) {
          tmpArray.push(currentReq);
        }
      })
    );

    setCurrentRequirements(tmpArray);
  }, [legalPhaseFullObjectArray]);

  return (
    <>
      <LegalRequirementDocumentDialogs
        configObject={configObject}
        legalTransaction={legalTransaction}
        currentReq={currentReq}
        documentArrayClient={documentArrayClient}
        setDocumentArrayClient={setDocumentArrayClient}
        documentArrayEmployees={documentArrayEmployees}
        isOpenClient={isOpenClient}
        setIsOpenClient={setIsOpenClient}
        isOpenEmployees={isOpenEmployees}
        setIsOpenEmployees={setIsOpenEmployees}
        isOpenUpload={isOpenUpload}
        setIsOpenUpload={setIsOpenUpload}
        handleCloseClient={handleCloseClient}
      />


      <Typography variant="h6" gutterBottom>
        Datenaustausch zu Maßnahmen
      </Typography>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={theme => ({
                '& th': {
                  backgroundColor: theme.palette.action.hover,
                  fontWeight: 600,
                },
              })}
            >
              <TableCell width="20%">
                Maßnahme
              </TableCell>
              <TableCell width="40%">
                Hochladen und herunterladen von eigenen Dateien
              </TableCell>
              <TableCell width="40%">
                Herunterladen von bereitgestellten Dateien
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentRequirements.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" fontStyle="italic">
                    Derzeitig sind keine Einträge verfügbar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {currentRequirements.map((requirement, index) => (
              <TableRow
                key={`currentReq-documentview-${requirement.idLegalRequirement}`}
                sx={theme => ({
                  backgroundColor:
                    index % 2 === 0
                      ? 'inherit'
                      : 'rgba(0, 0, 0, 0.03)',
                })}
              >
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography variant="body2">
                    {requirement.LegalRequirement}
                  </Typography>
                </TableCell>

                {/* Mandanten */}
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box flex={1}>
                      {renderDocumentList(
                        requirement.DocumentArrayClient,
                        !!requirement.canUploadFilesClient
                      )}
                    </Box>
                    {requirement.canUploadFilesClient &&
                      (
                        <Tooltip title="Hochladen und runterladen von eigenen Dateien">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenClient(requirement)}
                            aria-label="Mandantendaten verwalten"
                          >
                            <UploadFile fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Stack>
                </TableCell>

                {/* Mitarbeiter */}
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box flex={1}>
                      {renderDocumentList(
                        requirement.DocumentArrayEmployees,
                        !!requirement.canUploadFilesEmployees
                      )}
                    </Box>
                    {requirement.canUploadFilesEmployees &&
                      requirement.DocumentArrayEmployees &&
                      requirement.DocumentArrayEmployees.length > 0 && (

                      <Tooltip title="Runterladen von bereitgestellten Daten">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEmployees(requirement)}
                          aria-label="Daten für Mandanten verwalten"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </>
  );
};
